import { Link } from "react-router-dom";
import { Users, TrendingUp, Repeat, Zap, Clock, Award } from "lucide-react";

// Compact bytes formatter shared by storage widgets.
export const fmtBytes = (n) => {
  const b = Math.max(0, Number(n) || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(2)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const fmtSecs = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h === 0) return `${m}m ${(s % 60).toString().padStart(2, "0")}s`;
  return `${h}h ${m}m`;
};

// Section wrapper — consistent white "chunky-card" panel used across widgets.
export const Panel = ({ title, icon, testId, right, children, tone = "#fff" }) => (
  <section className="chunky-card p-4 sm:p-5" data-testid={testId} style={{ background: tone }}>
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      <h2 className="font-display text-base sm:text-lg flex items-center gap-2">
        {icon}
        {title}
      </h2>
      {right}
    </div>
    {children}
  </section>
);

// Retention D1/D7 average tiles + last-N-days bars.
export const RetentionCard = ({ retention }) => {
  if (!retention) {
    return (
      <Panel
        title="Retention (D1 · D7)"
        icon={<Repeat size={18} strokeWidth={3} style={{ color: "#5F27CD" }} />}
        testId="admin-retention"
      >
        <p className="text-sm" style={{ color: "#484848" }} data-testid="retention-loading">
          Loading retention…
        </p>
      </Panel>
    );
  }
  const cohorts = retention.cohorts || [];
  const d1Pct = Math.round((retention.avg_d1_rate || 0) * 100);
  const d7Pct = Math.round((retention.avg_d7_rate || 0) * 100);
  const hasReturns = cohorts.some(
    (c) => (c.d1 || 0) > 0 || (c.d7 || 0) > 0
  );
  return (
    <Panel
      title="Retention (D1 · D7)"
      icon={<Repeat size={18} strokeWidth={3} style={{ color: "#5F27CD" }} />}
      testId="admin-retention"
      right={
        <span className="text-xs" style={{ color: "#8a8a8a" }}>
          {retention.window_days}-day window
        </span>
      }
    >
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div
          className="chunky-card p-3 text-center"
          style={{ background: "#EFE7FF" }}
          data-testid="retention-d1-avg"
        >
          <div className="text-xs uppercase font-display" style={{ color: "#5F27CD" }}>
            Avg D1
          </div>
          <div className="font-display text-2xl">{d1Pct}%</div>
        </div>
        <div
          className="chunky-card p-3 text-center"
          style={{ background: "#E9FFE6" }}
          data-testid="retention-d7-avg"
        >
          <div className="text-xs uppercase font-display" style={{ color: "#5F27CD" }}>
            Avg D7
          </div>
          <div className="font-display text-2xl">{d7Pct}%</div>
        </div>
      </div>
      <div
        className="grid gap-1"
        style={{
          gridTemplateColumns: `repeat(${Math.max(1, cohorts.length)}, minmax(0, 1fr))`,
          height: 80,
        }}
        data-testid="retention-bars"
      >
        {cohorts.map((c) => {
          const d1h = Math.round((c.d1_rate || 0) * 70);
          const d7h = c.d7_rate == null ? 0 : Math.round((c.d7_rate || 0) * 70);
          return (
            <div
              key={c.day}
              className="flex items-end justify-center gap-0.5 h-full"
              title={`${c.day} — cohort ${c.cohort} | D1 ${Math.round((c.d1_rate || 0) * 100)}% | D7 ${c.d7_rate == null ? "—" : Math.round(c.d7_rate * 100) + "%"}`}
              data-testid={`retention-bar-${c.day}`}
            >
              <div
                style={{
                  width: 6,
                  height: Math.max(2, d1h),
                  background: "#5F27CD",
                  border: "2px solid #2b2b2b",
                }}
              />
              <div
                style={{
                  width: 6,
                  height: Math.max(2, d7h),
                  background: "#54D62C",
                  border: "2px solid #2b2b2b",
                  opacity: c.d7_rate == null ? 0.25 : 1,
                }}
              />
            </div>
          );
        })}
      </div>
      {!hasReturns && (
        <p
          className="text-xs text-center mt-2"
          data-testid="retention-empty"
          style={{ color: "#8a8a8a" }}
        >
          No returning visitors in this window yet — chart will fill in as
          cohorts age.
        </p>
      )}
      <div className="flex items-center justify-center gap-4 text-xs mt-2" style={{ color: "#484848" }}>
        <span className="flex items-center gap-1">
          <span
            style={{ width: 10, height: 10, background: "#5F27CD", display: "inline-block" }}
          />
          D1
        </span>
        <span className="flex items-center gap-1">
          <span
            style={{ width: 10, height: 10, background: "#54D62C", display: "inline-block" }}
          />
          D7
        </span>
      </div>
    </Panel>
  );
};

// Live playtime widget.
export const PlaytimeCard = ({ playtime }) => {
  if (!playtime) {
    return (
      <Panel
        title="Playtime pulse"
        icon={<Clock size={18} strokeWidth={3} style={{ color: "#5F27CD" }} />}
        testId="admin-playtime"
      >
        <p className="text-sm" data-testid="playtime-loading" style={{ color: "#484848" }}>
          Loading playtime…
        </p>
      </Panel>
    );
  }
  const maxBucket = Math.max(1, ...(playtime.histogram || []).map((b) => b.count));
  return (
    <Panel
      title="Playtime pulse"
      icon={<Clock size={18} strokeWidth={3} style={{ color: "#5F27CD" }} />}
      testId="admin-playtime"
    >
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div
          className="chunky-card p-2 text-center"
          style={{ background: "#E5F6FF" }}
          data-testid="playtime-active-now"
        >
          <div className="text-[10px] uppercase font-display" style={{ color: "#5F27CD" }}>
            Active now
          </div>
          <div className="font-display text-xl">{playtime.active_now ?? 0}</div>
        </div>
        <div
          className="chunky-card p-2 text-center"
          style={{ background: "#E9FFE6" }}
          data-testid="playtime-avg"
        >
          <div className="text-[10px] uppercase font-display" style={{ color: "#5F27CD" }}>
            Avg session
          </div>
          <div className="font-display text-xl">{fmtSecs(playtime.avg_seconds)}</div>
        </div>
        <div
          className="chunky-card p-2 text-center"
          style={{ background: "#FFF3D6" }}
          data-testid="playtime-max"
        >
          <div className="text-[10px] uppercase font-display" style={{ color: "#5F27CD" }}>
            Longest
          </div>
          <div className="font-display text-xl">{fmtSecs(playtime.max_seconds)}</div>
        </div>
      </div>
      <ul className="space-y-1" data-testid="playtime-histogram">
        {(playtime.histogram || []).map((b) => {
          const pct = Math.round((b.count / maxBucket) * 100);
          return (
            <li key={b.label} className="text-xs" data-testid={`playtime-bucket-${b.label}`}>
              <div className="flex justify-between mb-0.5">
                <span style={{ color: "#484848" }}>{b.label}</span>
                <span className="font-display">{b.count.toLocaleString()}</span>
              </div>
              <div
                style={{
                  height: 8,
                  background: "#eee",
                  border: "2px solid #2b2b2b",
                  borderRadius: 6,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{ width: `${pct}%`, height: "100%", background: "#5F27CD" }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
};

// Top referrers table.
export const ReferrersCard = ({ referrers }) => {
  const rows = referrers?.rows || [];
  return (
    <Panel
      title="Top referrers"
      icon={<Award size={18} strokeWidth={3} style={{ color: "#5F27CD" }} />}
      testId="admin-referrers"
      right={
        <span className="text-xs" style={{ color: "#8a8a8a" }}>
          {(referrers?.total_referred_visitors ?? 0).toLocaleString()} arrived via share
        </span>
      }
    >
      {rows.length === 0 ? (
        <p className="text-sm" style={{ color: "#484848" }} data-testid="referrers-empty">
          No one has shared a link yet.
        </p>
      ) : (
        <ol className="space-y-1" data-testid="referrers-list">
          {rows.map((r, i) => (
            <li
              key={r.visitor_id}
              className="flex items-center justify-between text-sm py-1"
              style={{ borderBottom: "1px dashed #eee" }}
              data-testid={`referrer-row-${i}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-display" style={{ width: 22 }}>
                  {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`}
                </span>
                <Link
                  to={`/admin/users/${r.visitor_id}`}
                  className="truncate underline"
                  style={{ color: "#5F27CD" }}
                >
                  {r.last_name || r.first_name || r.visitor_id.slice(0, 10) + "…"}
                </Link>
              </div>
              <span className="font-display">{r.referrals}</span>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
};

// Games funnel table: plays → wins → coins per game.
export const GamesFunnelCard = ({ funnel }) => {
  const rows = funnel?.rows || [];
  return (
    <Panel
      title="Games funnel"
      icon={<TrendingUp size={18} strokeWidth={3} style={{ color: "#5F27CD" }} />}
      testId="admin-games-funnel"
      right={
        <span className="text-xs" style={{ color: "#8a8a8a" }}>
          plays → wins → coins
        </span>
      }
    >
      {rows.length === 0 ? (
        <p className="text-sm" style={{ color: "#484848" }} data-testid="funnel-empty">
          No game plays in this window yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: "#FFB40033" }}>
              <tr style={{ borderBottom: "2px solid #2b2b2b" }}>
                <th className="text-left py-2 px-2">Game</th>
                <th className="text-right py-2 px-2">Plays</th>
                <th className="text-right py-2 px-2">Wins</th>
                <th className="text-right py-2 px-2">Win %</th>
                <th className="text-right py-2 px-2">Coins</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.game_id}
                  style={{ borderBottom: "1px solid #eee" }}
                  data-testid={`funnel-row-${r.game_id}`}
                >
                  <td className="py-2 px-2 font-display">{r.game_id}</td>
                  <td className="py-2 px-2 text-right">{r.plays.toLocaleString()}</td>
                  <td className="py-2 px-2 text-right">{r.wins.toLocaleString()}</td>
                  <td className="py-2 px-2 text-right">
                    {Math.round((r.win_rate || 0) * 100)}%
                  </td>
                  <td className="py-2 px-2 text-right">
                    {(r.coins || 0).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
};

// 7x24 hourly heatmap — when kids play the most.
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HEAT_STOPS = ["#F6F6F6", "#EFE7FF", "#D6C3FF", "#B39CFF", "#8E6BFF", "#5F27CD"];
const heatColor = (v, max) => {
  if (!max || v <= 0) return HEAT_STOPS[0];
  const t = Math.min(1, v / max);
  const idx = Math.min(HEAT_STOPS.length - 1, 1 + Math.floor(t * (HEAT_STOPS.length - 1)));
  return HEAT_STOPS[idx];
};

export const HeatmapCard = ({ heatmap }) => {
  if (!heatmap) {
    return (
      <Panel
        title="Hourly heatmap"
        icon={<Zap size={18} strokeWidth={3} style={{ color: "#5F27CD" }} />}
        testId="admin-heatmap"
      >
        <p className="text-sm" data-testid="heatmap-loading" style={{ color: "#484848" }}>
          Loading heatmap…
        </p>
      </Panel>
    );
  }
  const grid = heatmap.grid || [];
  const max = heatmap.max || 0;
  return (
    <Panel
      title="Hourly heatmap"
      icon={<Zap size={18} strokeWidth={3} style={{ color: "#5F27CD" }} />}
      testId="admin-heatmap"
      right={
        <span className="text-xs" style={{ color: "#8a8a8a" }}>
          plays per hour (UTC)
        </span>
      }
    >
      {max === 0 ? (
        <p className="text-sm" data-testid="heatmap-empty" style={{ color: "#484848" }}>
          No plays in this window yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table
            className="text-[10px]"
            style={{ borderSpacing: 2, borderCollapse: "separate", margin: "0 auto" }}
            data-testid="heatmap-table"
          >
            <thead>
              <tr>
                <th />
                {Array.from({ length: 24 }).map((_, h) => (
                  <th key={h} className="px-0.5 font-display" style={{ color: "#8a8a8a" }}>
                    {h % 3 === 0 ? h : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.map((row, dow) => (
                <tr key={dow}>
                  <td className="pr-1 font-display" style={{ color: "#484848" }}>
                    {DAY_LABELS[dow]}
                  </td>
                  {row.map((v, h) => (
                    <td
                      key={h}
                      title={`${DAY_LABELS[dow]} ${h}:00 — ${v} plays`}
                      style={{
                        background: heatColor(v, max),
                        width: 14,
                        height: 14,
                        border: "1px solid #2b2b2b",
                        borderRadius: 3,
                      }}
                      data-testid={`heatmap-cell-${dow}-${h}`}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
};

// Big stat tile shared across the dashboard header.
export const StatTile = ({ icon, label, value, bg, testId }) => (
  <div className="chunky-card p-3" style={{ background: bg }} data-testid={testId}>
    <div className="flex items-center gap-2" style={{ color: "#5F27CD" }}>
      {icon}
      <span className="text-[10px] uppercase font-display">{label}</span>
    </div>
    <div className="font-display text-2xl mt-1">{value}</div>
  </div>
);

export const StatTileIcons = { Users };
